// Tiny i18n helper for the widget. Reads window.MonogatariLocale (set by
// sections/monogatari-uploader.liquid from Shopify's `request.locale.iso_code`)
// and returns the matching string from web/src/i18n/strings.ts. Falls back
// to EN if the requested key or locale is missing.
//
// Use:
//   import { t } from "../i18n";
//   <h2>{t("app.missing_style.title")}</h2>
//   <p>{t("app.quota.left.many", { n: 3 })}</p>
//
// Placeholders are {brace} tokens replaced by the value at the matching key
// in the params object. No pluralization rules — pick the right key
// (`.one` vs `.many`) at the call site.

import { STRINGS, type Locale } from "./strings";

export type { Locale } from "./strings";

const FALLBACK: Locale = "en";

/** Theme Editor overrides — set by sections/monogatari-uploader.liquid as
 *  window.MonogatariStrings ({ "upload.tap": "…", … }). The fields in the
 *  Theme Editor hold JA copy, so overrides only apply when the locale is JA;
 *  EN visitors keep the bundled EN strings. Blank/missing = bundled JA. */
function getOverride(key: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  const o = (window as unknown as { MonogatariStrings?: Record<string, string> })
    .MonogatariStrings;
  const v = o?.[key];
  return typeof v === "string" && v !== "" ? v : undefined;
}

/** Detect locale from the global injected by the Liquid wrapper.
 *  Standalone (non-Shopify) renders return EN. */
export function getLocale(): Locale {
  if (typeof window === "undefined") return FALLBACK;
  const raw = (window as unknown as { MonogatariLocale?: string }).MonogatariLocale;
  if (!raw) return FALLBACK;
  const lower = raw.toLowerCase();
  if (lower === "ja" || lower.startsWith("ja-")) return "ja";
  return FALLBACK;
}

/** Translate a key, optionally interpolating {brace} placeholders. */
export function t(
  key: keyof typeof STRINGS,
  params?: Record<string, string | number>,
): string {
  const entry = STRINGS[key];
  if (!entry) {
    if (typeof console !== "undefined") {
      console.warn(`[i18n] missing key: ${String(key)}`);
    }
    return String(key);
  }
  const locale = getLocale();
  let s = entry[locale] ?? entry[FALLBACK];
  if (locale === "ja") {
    const override = getOverride(String(key));
    if (override !== undefined) s = override;
  }
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.split(`{${k}}`).join(String(v));
    }
  }
  return s;
}
